import { Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import type { InstrumentPanel } from 'lib-analytics-types';
import { CSSProperties, RefObject, useMemo } from 'react';
import { useMap } from 'react-use';

import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';

import { usePanelMeasurements } from '../../utils/usePanelMeasurements';

import { InstrumentComparate } from './components/comparate/instrumentComparate';
import { InstrumentComparisonProvider } from './instrumentComparison.context';
import styles from './instrumentComparison.module.scss';
import {
  InstrumentComparates,
  InstrumentComparateSide,
} from './instrumentComparison.types';

type InstrumentComparisonProps = {
  /** The instrument panel. */
  panel: InstrumentPanel;
  /** The ref to the panel DOM element, used to get its measurements. */
  panelRef: RefObject<HTMLDivElement>;
};

export const InstrumentComparison = ({
  panel,
  panelRef,
}: InstrumentComparisonProps): JSX.Element => {
  const { dashboard, context, period, filters, settings } =
    useAnalyticsState(panel);

  const { panelContentHeight } = usePanelMeasurements(panelRef);

  const panelStyle = useMemo(
    (): CSSProperties => ({
      height: panelContentHeight || 'initial',
    }),
    [panelContentHeight],
  );

  /**
   * A preselected context is one that cannot be changed i.e. not rendered as a
   * dropdown. For example when there is only one option available, or none at
   * all, or when the context is `hidden` but the values are `preselected`.
   *
   * In the case when the context is selectable, the second comparate receives
   * it as undefined initially, so the user has to select.
   */
  const hasPreselectedContext =
    (dashboard.context?.values ?? []).length <= 1 ||
    dashboard.context?.options?.visualization === 'hidden';

  // The list of comparates, i.e. things being compared to each other. The left side instrument data
  // is preloaded on mount. The right side instrument data is loaded on demand.
  const [comparates, { set: setComparate }] = useMap<InstrumentComparates>({
    left: {
      controls: {
        context,
        period,
        filters,
        settings,
      },
    },
    right: {
      controls: {
        context: hasPreselectedContext ? context : undefined,
        period,
        filters,
        settings,
      },
    },
  });

  return (
    <InstrumentComparisonProvider>
      <div className={styles.root}>
        <div className={styles.columns}>
          <Text.Paragraph variant="3" subdued>
            Compare
          </Text.Paragraph>
          <Text.Paragraph variant="3" subdued>
            With
          </Text.Paragraph>
        </div>

        <div className={classnames(styles.columns, styles.columnsGrow)}>
          {Object.entries(comparates).map(([side, comparate]) => (
            <InstrumentComparate
              key={side}
              panel={panel}
              side={side as InstrumentComparateSide}
              comparate={comparate}
              onUpdate={setComparate}
              panelStyle={panelStyle}
            />
          ))}
        </div>
      </div>
    </InstrumentComparisonProvider>
  );
};
